import { Fragment } from "react";
import { Box, Typography } from "@mui/material";
import { ANALYTICS } from "./analyticsTokens";
import type { AnalyticsSnapshot, PerformanceWindow } from "./analyticsApi";
import { ANALYTICS_WINDOWS, formatUpdatedAgo } from "./analyticsTable";
import {
  cacheAgePace,
  cacheContractLabel,
  cacheFreshnessCaption,
  cacheReadsCaption,
  cacheRows,
  formatAge,
  formatSeconds,
  hasAnyCacheActivity,
  hasAnyLoads,
  loadsCaption,
  paceOf,
  pastContractFor,
  performanceRows,
  probeSummary,
  refresherCaption,
  refresherFailed,
  slowestFor,
  type CacheCell,
  type CacheRow,
  type Pace,
  type PerformanceCell,
  type PerformanceRow,
  type Population,
} from "./performanceTable";

const SLOWEST_WINDOW = "7d";

const base = { fontFamily: ANALYTICS.font } as const;
const bodyText = {
  ...base,
  fontSize: 16,
  lineHeight: "24px",
  color: ANALYTICS.muted,
} as const;
const smallText = {
  ...base,
  fontSize: 12,
  lineHeight: "16px",
  color: ANALYTICS.disabled,
} as const;
const heading = {
  ...base,
  fontSize: 20,
  lineHeight: "28px",
  fontWeight: 700,
  color: ANALYTICS.ink,
} as const;
const eyebrow = {
  ...base,
  fontSize: 12,
  lineHeight: "16px",
  fontWeight: 700,
  letterSpacing: ANALYTICS.tracking.wider,
  textTransform: "uppercase",
  color: ANALYTICS.muted,
} as const;
const cardSx = {
  bgcolor: ANALYTICS.surface,
  borderRadius: "8px",
  border: `1px solid ${ANALYTICS.grey50}`,
} as const;
const paceSx: Record<Pace, { bgcolor: string; color: string }> = {
  green: { bgcolor: ANALYTICS.green50, color: ANALYTICS.green700 },
  yellow: { bgcolor: ANALYTICS.yellow100, color: ANALYTICS.yellow900 },
  red: { bgcolor: ANALYTICS.red600, color: ANALYTICS.surface },
};

type Props = { snapshot: AnalyticsSnapshot; now?: () => Date };

export function PerformanceTab({ snapshot, now = () => new Date() }: Props) {
  if (!snapshot.computed) {
    return (
      <Typography data-testid="admin-performance-uncounted" sx={bodyText}>
        Load times have not been computed yet. They are refreshed every 20
        minutes.
      </Typography>
    );
  }
  const windows = snapshot.performance ?? [];
  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: "32px" }}>
      <Typography data-testid="admin-performance-scope" sx={smallText}>
        How long each surface took to answer. Browser rows are what a person
        waited; server rows are the Lambda behind them.
      </Typography>
      <Section
        title="Users in the web app"
        population="users"
        windows={windows}
        empty="No loads timed yet."
      />
      <CachesSection windows={windows} now={now()} />
      <Section
        title="Refreshers in the background"
        population="background"
        windows={windows}
        empty="No refresh has run yet."
        note="Runs nobody waited for: the Lambdas that relist rosters, recompute growth tiles and rewrite monitoring records in a district's place."
      />
      <Section
        title="Adhoc probe"
        population="probe"
        windows={windows}
        empty="The probe has not run yet. Invoke the adhoc-performance-probe Lambda to time every district."
        summary={probeLine(windows, now())}
      />
    </Box>
  );
}

function probeLine(windows: PerformanceWindow[], now: Date): string | null {
  const { lastRunAt, districts } = probeSummary(windows, SLOWEST_WINDOW);
  if (!lastRunAt) return null;
  return `${formatUpdatedAgo(lastRunAt, now).replace(/^Updated/, "Last run")} · ${districts} ${districts === 1 ? "district" : "districts"}`;
}

type SectionProps = {
  title: string;
  population: Population;
  windows: PerformanceWindow[];
  empty: string;
  summary?: string | null;
  note?: string;
};

function Section({
  title,
  population,
  windows,
  empty,
  summary,
  note,
}: SectionProps) {
  const rows = performanceRows(windows, population);
  const populated = hasAnyLoads(windows, population);
  return (
    <Box
      component="section"
      data-testid="admin-performance-section"
      sx={{ display: "flex", flexDirection: "column", gap: "16px" }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "baseline",
          gap: "16px",
          flexWrap: "wrap",
        }}
      >
        <Typography component="h2" sx={heading}>
          {title}
        </Typography>
        {populated && summary && (
          <Typography data-testid="admin-performance-probe-run" sx={bodyText}>
            {summary}
          </Typography>
        )}
      </Box>
      {note && <Typography sx={smallText}>{note}</Typography>}
      {!populated && (
        <Typography data-testid="admin-performance-empty" sx={bodyText}>
          {empty}
        </Typography>
      )}
      {populated && (
        <>
          <RouteTable rows={rows} />
          <SlowestCard rows={rows} population={population} windows={windows} />
        </>
      )}
    </Box>
  );
}

const gridSx = {
  display: "grid",
  gridTemplateColumns: `minmax(220px, 1fr) repeat(${ANALYTICS_WINDOWS.length}, minmax(190px, 1fr))`,
  gap: "16px",
  px: "24px",
} as const;

function CachesSection({
  windows,
  now,
}: {
  windows: PerformanceWindow[];
  now: Date;
}) {
  const rows = cacheRows(windows);
  const populated = hasAnyCacheActivity(windows);
  return (
    <Box
      component="section"
      data-testid="admin-performance-caches"
      sx={{ display: "flex", flexDirection: "column", gap: "16px" }}
    >
      <Typography component="h2" sx={heading}>
        Caches against their contracts
      </Typography>
      <Typography sx={smallText}>
        Over the loads people waited for: how often each cache answered, how
        old the data it served was, and how its refresher has been doing. The
        roster snapshot and benchmark cache are warmed nightly, so their data
        should be under a day old; growth tiles and monitoring records are
        rewritten within the hour; the classification cache is current by
        construction, so only its hit rate counts. Ages are green within
        contract, yellow up to twice it and red past that.
      </Typography>
      {!populated && (
        <Typography data-testid="admin-performance-caches-empty" sx={bodyText}>
          No load has been served from a cache and no refresher has run yet.
        </Typography>
      )}
      {populated && (
        <>
          <Box
            data-testid="admin-performance-caches-table"
            role="table"
            sx={{ ...cardSx, overflowX: "auto" }}
          >
            <Box
              role="row"
              sx={{
                ...gridSx,
                py: "12px",
                borderBottom: `1px solid ${ANALYTICS.grey50}`,
              }}
            >
              <Typography role="columnheader" sx={eyebrow}>
                Cache · contract
              </Typography>
              {ANALYTICS_WINDOWS.map(({ key, label }) => (
                <Typography key={key} role="columnheader" sx={eyebrow}>
                  {label}
                </Typography>
              ))}
            </Box>
            {rows.map((row) => (
              <CacheRowView key={row.cache} row={row} now={now} />
            ))}
          </Box>
          <PastContractCard rows={rows} windows={windows} />
        </>
      )}
    </Box>
  );
}

function contractOf(row: CacheRow): number {
  return row.cells.find((cell) => cell !== null)?.contractMs ?? 0;
}

function CacheRowView({ row, now }: { row: CacheRow; now: Date }) {
  return (
    <Box
      role="row"
      data-testid="admin-performance-cache-row"
      sx={{
        ...gridSx,
        py: "16px",
        borderBottom: `1px solid ${ANALYTICS.grey50}`,
        "&:last-of-type": { borderBottom: 0 },
      }}
    >
      <Box role="rowheader" sx={{ display: "flex", flexDirection: "column" }}>
        <Typography
          data-testid="admin-performance-cache-label"
          sx={{
            ...base,
            fontSize: 16,
            lineHeight: "24px",
            fontWeight: 600,
            color: ANALYTICS.ink,
          }}
        >
          {row.label}
        </Typography>
        <Typography
          data-testid="admin-performance-cache-contract"
          sx={smallText}
        >
          {cacheContractLabel(contractOf(row))}
        </Typography>
      </Box>
      {row.cells.map((cell, i) => (
        <CacheCellView
          key={ANALYTICS_WINDOWS[i]!.key}
          cell={cell}
          windowLabel={ANALYTICS_WINDOWS[i]!.label}
          now={now}
        />
      ))}
    </Box>
  );
}

function CacheCellView({
  cell,
  windowLabel,
  now,
}: {
  cell: CacheCell | null;
  windowLabel: string;
  now: Date;
}) {
  const freshness = cell && cacheFreshnessCaption(cell);
  const refresher = cell && refresherCaption(cell.refresher, now);
  return (
    <Box
      role="cell"
      data-testid="admin-performance-cache-cell"
      aria-label={windowLabel}
      sx={{ display: "flex", flexDirection: "column", gap: "2px" }}
    >
      {cell === null ? (
        <Typography sx={{ ...bodyText, color: ANALYTICS.disabled }}>
          —
        </Typography>
      ) : (
        <>
          {cell.aged > 0 && cell.contractMs > 0 && (
            <Ages
              contractMs={cell.contractMs}
              ages={[
                ["p50", cell.p50AgeMs],
                ["p90", cell.p90AgeMs],
                ["oldest", cell.oldestAgeMs],
              ]}
            />
          )}
          <Typography
            data-testid="admin-performance-cache-reads"
            data-missed={cell.misses > 0 ? "true" : "false"}
            sx={smallText}
          >
            {cacheReadsCaption(cell)}
          </Typography>
          {freshness && (
            <Typography
              data-testid="admin-performance-cache-freshness"
              sx={smallText}
            >
              {freshness}
            </Typography>
          )}
          {refresher && (
            <Typography
              data-testid="admin-performance-cache-refresher"
              data-failed={refresherFailed(cell.refresher) ? "true" : "false"}
              sx={{
                ...smallText,
                color: refresherFailed(cell.refresher)
                  ? ANALYTICS.red600
                  : smallText.color,
              }}
            >
              {refresher}
            </Typography>
          )}
        </>
      )}
    </Box>
  );
}

function Ages({
  ages,
  contractMs,
}: {
  ages: [string, number][];
  contractMs: number;
}) {
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        flexWrap: "wrap",
        gap: "6px",
      }}
    >
      {ages.map(([label, ms], i) => (
        <Fragment key={label}>
          {i > 0 && " "}
          <Typography
            component="span"
            data-testid="admin-performance-cache-age"
            data-pace={cacheAgePace(ms, contractMs)}
            sx={{
              ...base,
              fontSize: 14,
              lineHeight: "20px",
              fontWeight: 600,
              p: "0 6px",
              borderRadius: "4px",
              whiteSpace: "nowrap",
              ...paceSx[cacheAgePace(ms, contractMs)],
            }}
          >
            {`${label} ${formatAge(ms)}`}
          </Typography>
        </Fragment>
      ))}
    </Box>
  );
}

function PastContractCard({
  rows,
  windows,
}: {
  rows: CacheRow[];
  windows: PerformanceWindow[];
}) {
  const groups = rows
    .map((row) => ({
      row,
      districts: pastContractFor(windows, SLOWEST_WINDOW, row.cache),
    }))
    .filter((g) => g.districts.length > 0);
  if (groups.length === 0) return null;
  return (
    <Box
      data-testid="admin-performance-past-contract"
      sx={{
        ...cardSx,
        px: "24px",
        py: "16px",
        display: "flex",
        flexDirection: "column",
        gap: "12px",
      }}
    >
      <Typography sx={eyebrow}>
        Districts served data past contract, last 7 days
      </Typography>
      {groups.map(({ row, districts }) => (
        <Box
          key={row.cache}
          sx={{ display: "flex", flexDirection: "column", gap: "4px" }}
        >
          <Typography
            sx={{
              ...base,
              fontSize: 14,
              lineHeight: "20px",
              fontWeight: 600,
              color: ANALYTICS.ink,
            }}
          >
            {row.label}
          </Typography>
          {districts.map((d) => (
            <Typography
              key={d.districtId}
              data-testid="admin-performance-past-contract-row"
              sx={bodyText}
            >
              {`${d.name ?? d.districtId} · ${d.reads} ${d.reads === 1 ? "read" : "reads"} · oldest ${formatAge(d.oldestAgeMs)}`}
            </Typography>
          ))}
        </Box>
      ))}
    </Box>
  );
}

function RouteTable({ rows }: { rows: PerformanceRow[] }) {
  return (
    <Box
      data-testid="admin-performance-table"
      role="table"
      sx={{ ...cardSx, overflowX: "auto" }}
    >
      <Box
        role="row"
        sx={{
          display: "grid",
          gridTemplateColumns: `minmax(220px, 1fr) repeat(${ANALYTICS_WINDOWS.length}, minmax(190px, 1fr))`,
          gap: "16px",
          px: "24px",
          py: "12px",
          borderBottom: `1px solid ${ANALYTICS.grey50}`,
        }}
      >
        <Typography role="columnheader" sx={eyebrow}>
          Surface
        </Typography>
        {ANALYTICS_WINDOWS.map(({ key, label }) => (
          <Typography key={key} role="columnheader" sx={eyebrow}>
            {label}
          </Typography>
        ))}
      </Box>
      {rows.map((row) => (
        <Box
          key={row.route}
          role="row"
          data-testid="admin-performance-row"
          sx={{
            display: "grid",
            gridTemplateColumns: `minmax(220px, 1fr) repeat(${ANALYTICS_WINDOWS.length}, minmax(190px, 1fr))`,
            gap: "16px",
            px: "24px",
            py: "16px",
            borderBottom: `1px solid ${ANALYTICS.grey50}`,
            "&:last-of-type": { borderBottom: 0 },
          }}
        >
          <Typography
            role="rowheader"
            data-testid="admin-performance-label"
            sx={{
              ...base,
              fontSize: 16,
              lineHeight: "24px",
              fontWeight: 600,
              color: ANALYTICS.ink,
            }}
          >
            {row.label}
          </Typography>
          {row.cells.map((cell, i) => (
            <Cell
              key={ANALYTICS_WINDOWS[i]!.key}
              cell={cell}
              windowLabel={ANALYTICS_WINDOWS[i]!.label}
            />
          ))}
        </Box>
      ))}
    </Box>
  );
}

function Cell({
  cell,
  windowLabel,
}: {
  cell: PerformanceCell | null;
  windowLabel: string;
}) {
  return (
    <Box
      role="cell"
      data-testid="admin-performance-cell"
      aria-label={windowLabel}
      sx={{ display: "flex", flexDirection: "column", gap: "2px" }}
    >
      {cell === null ? (
        <Typography sx={{ ...bodyText, color: ANALYTICS.disabled }}>
          —
        </Typography>
      ) : (
        <>
          {cell.count > cell.unconfigured && (
            <Times
              times={[
                ["p50", cell.p50Ms],
                ["p90", cell.p90Ms],
                ["max", cell.maxMs],
              ]}
            />
          )}
          <Typography
            data-testid="admin-performance-loads"
            data-failed={cell.failures > 0 ? "true" : "false"}
            sx={{
              ...smallText,
              color: cell.failures > 0 ? ANALYTICS.red600 : smallText.color,
            }}
          >
            {loadsCaption(cell)}
          </Typography>
        </>
      )}
    </Box>
  );
}

function Times({ times }: { times: [string, number][] }) {
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        flexWrap: "wrap",
        gap: "6px",
      }}
    >
      {times.map(([label, ms], i) => (
        <Fragment key={label}>
          {i > 0 && " "}
          <Typography
            component="span"
            data-testid="admin-performance-time"
            data-pace={paceOf(ms)}
            sx={{
              ...base,
              fontSize: 14,
              lineHeight: "20px",
              fontWeight: 600,
              p: "0 6px",
              borderRadius: "4px",
              whiteSpace: "nowrap",
              ...paceSx[paceOf(ms)],
            }}
          >
            {`${label} ${formatSeconds(ms)}`}
          </Typography>
        </Fragment>
      ))}
    </Box>
  );
}

function SlowestCard({
  rows,
  population,
  windows,
}: {
  rows: PerformanceRow[];
  population: Population;
  windows: PerformanceWindow[];
}) {
  const groups = rows
    .map((row) => ({
      row,
      districts: slowestFor(windows, population, SLOWEST_WINDOW, row.route),
    }))
    .filter((g) => g.districts.length > 0);
  if (groups.length === 0) return null;
  return (
    <Box
      data-testid="admin-performance-slowest"
      sx={{
        ...cardSx,
        px: "24px",
        py: "16px",
        display: "flex",
        flexDirection: "column",
        gap: "12px",
      }}
    >
      <Typography sx={eyebrow}>Slowest districts, last 7 days</Typography>
      {groups.map(({ row, districts }) => (
        <Box
          key={row.route}
          sx={{ display: "flex", flexDirection: "column", gap: "4px" }}
        >
          <Typography
            sx={{
              ...base,
              fontSize: 14,
              lineHeight: "20px",
              fontWeight: 600,
              color: ANALYTICS.ink,
            }}
          >
            {row.label}
          </Typography>
          {districts.map((d) => (
            <Box
              key={d.districtId}
              data-testid="admin-performance-slowest-row"
              sx={{
                display: "flex",
                alignItems: "center",
                flexWrap: "wrap",
                gap: "6px",
              }}
            >
              <Typography sx={bodyText}>
                {`${d.name ?? d.districtId} · ${loadsCaption({ count: d.count, failures: 0, live: d.live, liveLoads: d.liveLoads })}`}
              </Typography>{" "}
              <Times
                times={[
                  ["p90", d.p90Ms],
                  ["max", d.maxMs],
                ]}
              />
            </Box>
          ))}
        </Box>
      ))}
    </Box>
  );
}
